import { Controller } from "@hotwired/stimulus";

// Modern reader contents navigation (assets/_reader_nav, layouts/modern_reader).
// docs/book-reader-modern.md, "Navigation".
//
// Desktop (≥768px): the panel is a non-modal sidebar that slides over the rail.
//   Its open/collapsed choice is remembered (localStorage "readerNav") and kept
//   as `reader-nav-open` on <html>, which Turbo doesn't replace, so the state
//   carries across Drive visits. The inline <head> script in the layout applies
//   it before first paint (open only where CLEARS_OPEN_PANEL matches).
// Mobile: the panel is a modal drawer (`reader-nav-drawer-open` on <html>):
//   backdrop, focus trap, Esc / ✕ / backdrop tap / swipe left to close, focus
//   back to ☰. Never remembered.
//
// Sections come from the page's own chapter outline (built client-side by
// chapter-outline), so they're read once the outline exists and listed in the
// rail and under the current chapter. Picking one clicks the outline's own link
// so its scroll offset and reading-focus handling apply.
const DESKTOP = "(min-width: 768px)";
const CLEARS_OPEN_PANEL = "(min-width: 1600px)"; // 970px column + 2 × 316px panel
const STORAGE_KEY = "readerNav";
const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default class extends Controller {
  static targets = ["panel", "toggle", "railSections", "panelSections", "chip", "label"];

  connect() {
    this.root = document.documentElement;
    this.desktop = window.matchMedia(DESKTOP);
    this.onBreakpoint = () => this.#closeDrawer({ restoreFocus: false });
    this.desktop.addEventListener("change", this.onBreakpoint);

    this.onScroll = () => {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.#trackScroll();
      });
    };
    window.addEventListener("scroll", this.onScroll, { passive: true });

    this.#syncExpanded();
    this.#waitForOutline();
    // enable the slide only after first paint, so a page load doesn't animate
    requestAnimationFrame(() => requestAnimationFrame(() => this.element.classList.add("reader-nav-animate")));
  }

  disconnect() {
    this.desktop.removeEventListener("change", this.onBreakpoint);
    window.removeEventListener("scroll", this.onScroll);
    this.outlineObserver?.disconnect();
    this.root.classList.remove("reader-nav-drawer-open");
  }

  // ---- open / close ---------------------------------------------------------

  toggle(event) {
    this.opener = event?.currentTarget;
    if (this.desktop.matches) {
      this.root.classList.contains("reader-nav-open") ? this.close() : this.#openPanel();
    } else {
      this.root.classList.contains("reader-nav-drawer-open") ? this.close() : this.#openDrawer();
    }
  }

  close() {
    if (this.desktop.matches) {
      this.root.classList.remove("reader-nav-open");
      this.#remember("collapsed");
      this.#syncExpanded();
      this.#focusToggle();
    } else {
      this.#closeDrawer({ restoreFocus: true });
    }
  }

  // Reading Link opens its own panel; get the mobile drawer out of the way.
  closeDrawer() {
    if (!this.desktop.matches) this.#closeDrawer({ restoreFocus: false });
  }

  keydown(event) {
    if (event.key === "Escape") {
      event.preventDefault();
      this.close();
    } else if (event.key === "Tab" && this.#drawerOpen()) {
      this.#trapFocus(event);
    }
  }

  touchstart(event) {
    this.touchX = event.touches[0].clientX;
    this.touchY = event.touches[0].clientY;
  }

  touchend(event) {
    if (!this.#drawerOpen() || this.touchX == null) return;
    const dx = event.changedTouches[0].clientX - this.touchX;
    const dy = event.changedTouches[0].clientY - this.touchY;
    this.touchX = null;
    if (dx < -60 && Math.abs(dx) > Math.abs(dy)) this.close();
  }

  #openPanel() {
    this.root.classList.add("reader-nav-open");
    this.#remember("open");
    this.#syncExpanded();
    this.panelTarget.focus({ preventScroll: true });
  }

  #openDrawer() {
    this.root.classList.add("reader-nav-drawer-open");
    this.#syncExpanded();
    const first = this.panelTarget.querySelector(".reader-nav-close");
    (first || this.panelTarget).focus({ preventScroll: true });
  }

  #closeDrawer({ restoreFocus }) {
    if (!this.#drawerOpen()) return;
    this.root.classList.remove("reader-nav-drawer-open");
    this.#syncExpanded();
    if (restoreFocus) this.#focusToggle();
  }

  #drawerOpen() {
    return this.root.classList.contains("reader-nav-drawer-open");
  }

  #trapFocus(event) {
    const items = Array.from(this.panelTarget.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  #focusToggle() {
    const visible = this.toggleTargets.find((el) => el.offsetParent !== null);
    (this.opener && this.opener.offsetParent !== null ? this.opener : visible)?.focus({ preventScroll: true });
  }

  #syncExpanded() {
    const open = this.desktop.matches ? this.root.classList.contains("reader-nav-open") : this.#drawerOpen();
    this.toggleTargets.forEach((el) => el.setAttribute("aria-expanded", String(open)));
    this.panelTarget.inert = !open;
  }

  #remember(state) {
    try {
      localStorage.setItem(STORAGE_KEY, state);
    } catch (e) {
      // private mode / blocked storage: the choice just isn't remembered
    }
  }

  // ---- sections -------------------------------------------------------------

  #waitForOutline() {
    if (this.#buildSections()) return;
    const main = document.getElementById("main");
    if (!main) return;
    this.outlineObserver = new MutationObserver(() => {
      if (this.#buildSections()) this.outlineObserver.disconnect();
    });
    this.outlineObserver.observe(main, { childList: true, subtree: true });
  }

  #buildSections() {
    const links = Array.from(document.querySelectorAll(".chapter-outline li > a"));
    if (links.length === 0) return false;

    this.sections = links.map((link) => {
      const number = link.querySelector(".toc-number")?.textContent.trim() || "";
      const clone = link.cloneNode(true);
      clone.querySelector(".toc-number")?.remove();
      const title = clone.textContent.trim();
      const id = (link.getAttribute("href") || "").replace(/^#/, "");
      return { link, number, title, id, sub: link.closest("li").classList.contains("toc-sub") };
    });

    if (this.hasPanelSectionsTarget) {
      this.panelSectionsTarget.replaceChildren(...this.sections.map((section) => this.#panelItem(section)));
      this.panelSectionsTarget.hidden = false;
    }
    // The rail lists one level of sections, the shallowest that carries the
    // numbers. Usually that's the top level (a long chapter has dozens of
    // sub-sections). But when the top-level headings are unnumbered and only
    // their sub-sections are (the Introduction: 0.1–0.11 under two unnumbered
    // headings), it lists those sub-sections, plus any heading with nothing
    // under it (Review Exercises). Every section points at the rail entry it
    // highlights: its parent, itself, or a heading's first sub-section.
    const topLevelNumbered = this.sections.some((section) => !section.sub && section.number);
    const rail = [];
    let parent = null;
    this.sections.forEach((section, index) => {
      if (!section.sub) {
        parent = section;
        const hasChildren = this.sections[index + 1]?.sub;
        if (topLevelNumbered || !hasChildren) rail.push(section);
        section.railId = topLevelNumbered || !hasChildren ? section.id : this.sections[index + 1].id;
      } else {
        if (!topLevelNumbered) rail.push(section);
        section.railId = topLevelNumbered && parent ? parent.id : section.id;
      }
    });
    if (this.hasRailSectionsTarget) {
      this.railSectionsTarget.replaceChildren(...rail.map((section) => this.#railItem(section)));
    }

    // "Chapter 2" from the theme hero's eyebrow — only when it names a chapter
    // (the Introduction's eyebrow is the book's name, not a chapter label)
    const eyebrow = document.querySelector(".module-hero h1[data-eyebrow]")?.dataset.eyebrow?.trim();
    const isChapter = /^chapter\s+\S+/i.test(eyebrow || "");
    this.chapterLabel = isChapter ? eyebrow : "";
    if (this.hasChipTarget && isChapter) {
      this.chipTarget.textContent = eyebrow.replace(/^chapter\s*/i, "CH ");
      this.chipTarget.hidden = false;
    }

    this.#trackScroll();
    return true;
  }

  #panelItem(section) {
    const li = document.createElement("li");
    if (section.sub) li.className = "is-sub";
    const a = document.createElement("a");
    a.href = `#${section.id}`;
    a.dataset.sectionId = section.id;
    if (section.number) {
      const num = document.createElement("span");
      num.className = "reader-toc-number";
      num.textContent = section.number;
      a.appendChild(num);
    }
    a.append(section.title);
    a.addEventListener("click", (event) => this.#jump(event, section));
    li.appendChild(a);
    return li;
  }

  #railItem(section) {
    const li = document.createElement("li");
    const a = document.createElement("a");
    a.href = `#${section.id}`;
    a.dataset.railSectionId = section.id;
    a.textContent = section.number || section.title.slice(0, 3);
    a.title = section.number ? `${section.number} ${section.title}` : section.title;
    a.setAttribute("aria-label", a.title);
    a.addEventListener("click", (event) => this.#jump(event, section));
    li.appendChild(a);
    return li;
  }

  #jump(event, section) {
    event.preventDefault();
    if (this.#drawerOpen()) {
      this.#closeDrawer({ restoreFocus: false });
    } else if (this.desktop.matches && !window.matchMedia(CLEARS_OPEN_PANEL).matches &&
               this.root.classList.contains("reader-nav-open")) {
      // the panel overlays the text below the clearing width: get it out of the way
      this.root.classList.remove("reader-nav-open");
      this.#syncExpanded();
    }
    section.link.click();
  }

  // ---- scroll: current section ----------------------------------------------

  #trackScroll() {
    if (!this.sections) return;

    const line = 140; // just under the fixed bar
    let current = null;
    for (const section of this.sections) {
      const heading = document.getElementById(section.id);
      if (!heading) continue;
      if (heading.getBoundingClientRect().top <= line) current = section;
      else break;
    }
    if (current === this.currentSection) return;
    this.currentSection = current;

    this.element.querySelectorAll("[data-section-id]").forEach((el) => {
      const on = current && el.dataset.sectionId === current.id;
      el.classList.toggle("is-current-section", !!on);
      if (on) el.setAttribute("aria-current", "location");
      else el.removeAttribute("aria-current");
    });
    this.element.querySelectorAll("[data-rail-section-id]").forEach((el) => {
      const on = current && el.dataset.railSectionId === current.railId;
      el.classList.toggle("is-current-section", !!on);
      if (on) el.setAttribute("aria-current", "location");
      else el.removeAttribute("aria-current");
    });
    if (current && this.hasRailSectionsTarget) {
      this.railSectionsTarget.querySelector(".is-current-section")?.scrollIntoView({ block: "nearest" });
    }

    if (this.hasLabelTarget) {
      const parts = [this.chapterLabel, current?.number ? `Section ${current.number}` : current?.title].filter(Boolean);
      this.labelTarget.textContent = parts.join(" · ");
      this.labelTarget.hidden = parts.length === 0;
    }
  }
}
